import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";

import { toast } from "sonner";

import api, {
    formatApiError,
} from "@/lib/api";

import { useLang } from "@/contexts/LangContext";

import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";


const MOYASAR_CSS =
    "https://cdn.moyasar.com/mpf/1.7.3/moyasar.css";

const MOYASAR_JS =
    "https://cdn.moyasar.com/mpf/1.7.3/moyasar.js";


function loadMoyasarAssets() {
    return new Promise((resolve, reject) => {
        if (!document.querySelector(
            'link[data-eyadney-moyasar="css"]'
        )) {
            const css = document.createElement("link");

            css.rel = "stylesheet";
            css.href = MOYASAR_CSS;

            css.setAttribute(
                "data-eyadney-moyasar",
                "css"
            );

            document.head.appendChild(css);
        }

        if (window.Moyasar) {
            resolve();
            return;
        }

        const existingScript =
            document.querySelector(
                'script[data-eyadney-moyasar="js"]'
            );

        if (existingScript) {
            existingScript.addEventListener(
                "load",
                () => resolve(),
                { once: true }
            );

            existingScript.addEventListener(
                "error",
                () => reject(
                    new Error(
                        "Could not load Moyasar"
                    )
                ),
                { once: true }
            );

            return;
        }

        const script =
            document.createElement("script");

        script.src = MOYASAR_JS;
        script.async = true;

        script.setAttribute(
            "data-eyadney-moyasar",
            "js"
        );

        script.onload = () => {
            if (window.Moyasar) {
                resolve();
            } else {
                reject(
                    new Error(
                        "Moyasar library did not initialise"
                    )
                );
            }
        };

        script.onerror = () => {
            reject(
                new Error(
                    "Could not load Moyasar"
                )
            );
        };

        document.head.appendChild(script);
    });
}


export default function Payment() {
    const { orderId } = useParams();
    const { lang } = useLang();

    const navigate = useNavigate();

    const [searchParams] =
        useSearchParams();

    const paymentId =
        searchParams.get("id");

    const formRef = useRef(null);
    const verificationStartedRef = useRef(false);
    const [config, setConfig] =
        useState(null);

    const [loading, setLoading] =
        useState(true);

    const [verifying, setVerifying] =
        useState(false);

    const [result, setResult] =
        useState(null);

    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");

    const publishableKey =
        process.env
            .REACT_APP_MOYASAR_PUBLISHABLE_KEY;


    // =====================================================
    // 1. Load order/payment configuration from our backend
    // =====================================================

    useEffect(() => {
        let cancelled = false;

        const loadConfig = async () => {
            setLoading(true);
            setErrorMessage("");

            try {
                const { data } =
                    await api.get(
                        `/payments/moyasar/config/${orderId}`
                    );

                if (cancelled) {
                    return;
                }

                setConfig(data);

                if (
                    data.payment_status === "paid"
                ) {
                    setResult({
                        payment_status: "paid",
                        status: "confirmed",
                    });
                }

            } catch (error) {
                if (cancelled) {
                    return;
                }

                setErrorMessage(
                    formatApiError(
                        error.response
                            ?.data
                            ?.detail
                    )
                    || error.message
                    || "Could not load order"
                );

            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadConfig();

        return () => {
            cancelled = true;
        };

    }, [orderId]);


    // =====================================================
    // 2. Verify returned Moyasar payment
    // =====================================================

    const verifyPayment =
        useCallback(async () => {
            if (!paymentId) {
                return;
            }

            setVerifying(true);
            setErrorMessage("");

            try {
                const { data } =
                    await api.post(
                        `/payments/moyasar/verify/${orderId}`,
                        {
                            payment_id:
                                paymentId,
                        }
                    );

                setResult(data);

                if (
                    data.payment_status
                    === "paid"
                ) {
                    toast.success(
                        lang === "ar"
                            ? "تم الدفع بنجاح"
                            : "Payment completed successfully"
                    );

                } else if (
                    data.payment_status
                    === "failed"
                ) {
                    toast.error(
                        lang === "ar"
                            ? "لم تكتمل عملية الدفع"
                            : "Payment was not completed"
                    );
                }

            } catch (error) {
                setErrorMessage(
                    formatApiError(
                        error.response
                            ?.data
                            ?.detail
                    )
                    || error.message
                    || "Payment verification failed"
                );

            } finally {
                setVerifying(false);
            }
        }, [
            lang,
            orderId,
            paymentId,
        ]);


    useEffect(() => {
    if (!paymentId) {
        return;
    }

    if (verificationStartedRef.current) {
        return;
    }

    verificationStartedRef.current = true;

    verifyPayment();
}, [
    paymentId,
    verifyPayment,
]);


    // =====================================================
    // 3. Initialise Moyasar payment form
    // =====================================================

    useEffect(() => {
        if (
            !config
            || paymentId
            || result?.payment_status === "paid"
        ) {
            return;
        }

        // IMPORTANT:
        // formRef belongs ONLY in this effect.
        const formElement =
            formRef.current;

        if (!formElement) {
            return;
        }

        let cancelled = false;

        const initialiseMoyasar =
            async () => {

                if (!publishableKey) {
                    setErrorMessage(
                        lang === "ar"
                            ? "مفتاح ميسر العام غير موجود"
                            : "Moyasar publishable key is missing"
                    );

                    return;
                }

                try {
                    await loadMoyasarAssets();

                    if (cancelled) {
                        return;
                    }

                    if (!window.Moyasar) {
                        throw new Error(
                            "Moyasar form is unavailable"
                        );
                    }

                    formElement.innerHTML =
                        "";

                    const callbackUrl =
                        `${window.location.origin}/payment/${orderId}`;

                    window.Moyasar.init({
                        element:
                            formElement,

                        amount:
                            Number(
                                config.amount_halalas
                            ),

                        currency:
                            config.currency
                            || "SAR",

                        description:
                            `Souq Madar Order ${orderId}`,

                        publishable_api_key:
                            publishableKey,

                        callback_url:
                            callbackUrl,

                        methods: [
                            "creditcard",
                        ],

                        supported_networks: [
                            "mada",
                            "visa",
                            "mastercard",
                        ],

                        language:
                            lang === "ar"
                                ? "ar"
                                : "en",

                        fixed_width:
                            false,

                        metadata: {
                            order_id:
                                orderId,
                        },

                        on_completed:
                            async function (
                                payment
                            ) {
                                if (!payment?.id) {
                                    return;
                                }

                                try {
                                    await api.post(
                                        "/payments/moyasar/link",
                                        {
                                            order_id:
                                                orderId,

                                            payment_id:
                                                payment.id,
                                        }
                                    );

                                } catch (error) {
                                    console.error(
                                        "Could not save Moyasar payment ID",
                                        error
                                    );
                                }
                            },

                        on_failure:
                            async function (
                                error
                            ) {
                                console.error(
                                    "Moyasar form error:",
                                    error
                                );

                                toast.error(
                                    lang === "ar"
                                        ? "تعذر بدء عملية الدفع"
                                        : "Could not start payment"
                                );
                            },
                    });

                } catch (error) {
                    if (cancelled) {
                        return;
                    }

                    console.error(error);

                    setErrorMessage(
                        lang === "ar"
                            ? "تعذر تحميل نموذج الدفع من ميسر"
                            : "Could not load Moyasar payment form"
                    );
                }
            };

        initialiseMoyasar();

        return () => {
            cancelled = true;
            formElement.innerHTML = "";
        };

    }, [
        config,
        lang,
        orderId,
        paymentId,
        publishableKey,
        result?.payment_status,
    ]);


    // =====================================================
    // UI
    // =====================================================

    if (loading) {
        return (
            <Layout>
                <div className="max-w-xl mx-auto px-4 py-20 text-center">
                    <p className="text-muted-foreground">
                        {lang === "ar"
                            ? "جاري تجهيز الدفع..."
                            : "Preparing payment..."
                        }
                    </p>
                </div>
            </Layout>
        );
    }


    if (
        verifying
        && !result
    ) {
        return (
            <Layout>
                <div className="max-w-xl mx-auto px-4 py-20 text-center">

                    <h1 className="text-2xl font-black text-espresso">
                        {lang === "ar"
                            ? "جاري التحقق من عملية الدفع"
                            : "Verifying payment"
                        }
                    </h1>

                    <p className="mt-3 text-muted-foreground">
                        {lang === "ar"
                            ? "يرجى عدم إغلاق الصفحة."
                            : "Please do not close this page."
                        }
                    </p>

                </div>
            </Layout>
        );
    }


    if (
        result?.payment_status
        === "paid"
    ) {
        return (
            <Layout>
                <div className="max-w-xl mx-auto px-4 py-20">

                    <div className="bg-white border border-border rounded-3xl p-8 text-center">

                        <div className="text-5xl">
                            ✓
                        </div>

                        <h1 className="mt-4 text-3xl font-black text-espresso">
                            {lang === "ar"
                                ? "تم الدفع بنجاح"
                                : "Payment successful"
                            }
                        </h1>

                        <p className="mt-3 text-muted-foreground">
                            {lang === "ar"
                                ? "تم تأكيد طلبك بنجاح."
                                : "Your order has been confirmed."
                            }
                        </p>

                        <p className="mt-2 text-sm text-muted-foreground">
                            {orderId}
                        </p>

                        <Button
                            onClick={() =>
                                navigate(
                                    "/orders",
                                    {
                                        replace:
                                            true,
                                    }
                                )
                            }
                            className="mt-6 rounded-full bg-terracotta text-white"
                        >
                            {lang === "ar"
                                ? "عرض طلباتي"
                                : "View my orders"
                            }
                        </Button>

                    </div>

                </div>
            </Layout>
        );
    }


    if (
        result?.payment_status
        === "failed"
    ) {
        return (
            <Layout>
                <div className="max-w-xl mx-auto px-4 py-20">

                    <div className="bg-white border border-border rounded-3xl p-8 text-center">

                        <div className="text-5xl">
                            ×
                        </div>

                        <h1 className="mt-4 text-3xl font-black text-espresso">
                            {lang === "ar"
                                ? "فشلت عملية الدفع"
                                : "Payment failed"
                            }
                        </h1>

                        <p className="mt-3 text-muted-foreground">
                            {lang === "ar"
                                ? "لم يتم تأكيد الطلب كطلب مدفوع."
                                : "The order was not confirmed as paid."
                            }
                        </p>

                        <Button
                            onClick={() =>
                                navigate(
                                    "/orders",
                                    {
                                        replace:
                                            true,
                                    }
                                )
                            }
                            variant="outline"
                            className="mt-6 rounded-full"
                        >
                            {lang === "ar"
                                ? "العودة إلى طلباتي"
                                : "Back to my orders"
                            }
                        </Button>

                    </div>

                </div>
            </Layout>
        );
    }


    if (errorMessage) {
        return (
            <Layout>
                <div className="max-w-xl mx-auto px-4 py-20">

                    <div className="bg-white border border-border rounded-3xl p-8 text-center">

                        <h1 className="text-2xl font-black text-espresso">
                            {lang === "ar"
                                ? "تعذر إكمال الدفع"
                                : "Unable to continue payment"
                            }
                        </h1>

                        <p className="mt-4 text-sm text-destructive">
                            {errorMessage}
                        </p>

                        {paymentId && (
                            <Button
                                onClick={
                                    verifyPayment
                                }
                                disabled={
                                    verifying
                                }
                                className="mt-6 rounded-full bg-terracotta text-white"
                            >
                                {verifying
                                    ? "..."
                                    : lang === "ar"
                                        ? "إعادة التحقق"
                                        : "Verify again"
                                }
                            </Button>
                        )}

                    </div>

                </div>
            </Layout>
        );
    }


    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 md:px-8 py-10">

                <div className="text-center">

                    <h1 className="text-3xl md:text-4xl font-black text-espresso">
                        {lang === "ar"
                            ? "الدفع"
                            : "Payment"
                        }
                    </h1>

                    <p className="mt-2 text-muted-foreground">
                        {lang === "ar"
                            ? "أكمل عملية الدفع الآمنة عبر ميسر"
                            : "Complete your secure payment with Moyasar"
                        }
                    </p>

                </div>


                <div className="mt-8 bg-white border border-border rounded-3xl p-5 md:p-8">

                    <div className="mb-6 bg-muted/30 rounded-2xl p-4">

                        <div className="flex justify-between gap-4">

                            <span className="text-muted-foreground">
                                {lang === "ar"
                                    ? "رقم الطلب"
                                    : "Order"
                                }
                            </span>

                            <span className="font-bold text-espresso">
                                {orderId}
                            </span>

                        </div>


                        <div className="flex justify-between gap-4 mt-2">

                            <span className="text-muted-foreground">
                                {lang === "ar"
                                    ? "المبلغ"
                                    : "Amount"
                                }
                            </span>

                            <span className="font-black text-terracotta">
                                {Number(
                                    config?.amount
                                    || 0
                                ).toFixed(2)}
                                {" "}
                                {config?.currency
                                    || "SAR"
                                }
                            </span>

                        </div>

                    </div>


                    <div
                        ref={formRef}
                        className="mysr-form"
                    />


                    <p className="mt-5 text-xs text-muted-foreground text-center">
                        {lang === "ar"
                            ? "معلومات البطاقة يتم التعامل معها مباشرة بواسطة ميسر ولا يتم حفظها في سوق مدار."
                            : "Card details are handled directly by Moyasar and are not stored by Souq Madar."
                        }
                    </p>

                </div>

            </div>
        </Layout>
    );
}